import { useNavigate } from "react-router-dom";
import { Version } from "../types/version";
import { EntityController } from "./entityController";
import { extractReferenceKey } from "../components/forms/fields/basic/EntitySelectionField";
import useVersionLogic from "../hooks/logic/versionLogic";
import useVersionQuery from "../hooks/query/useVersionQuery";

export interface VersionController extends EntityController<Version> {
  displayName: (data: Version) => string;
  viewTitle: (data: Version) => string;
  editTitle: (data: Version) => string;
}

const useVersionController = (
  id: string | null,
  systemLanguageType: Version["system_language_type"]
): VersionController => {
  const { useFetchById, useCreate, useDeleteById, useUpdate } =
    useVersionQuery(systemLanguageType);
  const { userEnums, metadataFieldNames, defaultVersion, getDisplayName } =
    useVersionLogic(systemLanguageType);
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const deleteById = useDeleteById();
  const navigate = useNavigate();

  const fetchData = async (): Promise<Version> => {
    if (!id) {
      return defaultVersion;
    }
    const res = await fetchById.refetch();
    const versionData = res.data;
    if (!versionData || res.isError) {
      throw new Error("Version data not found");
    }
    return versionData;
  };

  const isCreateMode = id === null;

  const displayName = (data: Version) => getDisplayName(data);

  const viewTitle = (data: Version) => displayName(data);

  const editTitle = (data: Version) =>
    `${isCreateMode ? "Create New" : "Edit"} ${displayName(data)}`;

  const onSubmit = async (data: Partial<Version>) => {
    const updateData: Partial<Version> = {
      internal_notes: data.internal_notes,
      notes: data.notes,
      collection: data.collection,
      vol_number: data.vol_number,
      vol_name: data.vol_name,
      folio_start: data.folio_start,
      folio_end: data.folio_end,
      completeness: data.completeness,
      name: data.name,
      work_id: extractReferenceKey(data.work),
      source_language: data.source_language,
      version_date: data.version_date,
      title: data.title,
      catalog: data.catalog,
      transfered_collection: data.transfered_collection,
      doxographical_category: data.doxographical_category,
      language: data.language,
      status_of_indic_origin: data.status_of_indic_origin,
      link_to_other_databases: data.link_to_other_databases,
      date_of_composition: data.date_of_composition,
      attributes_metadata: {
        ...defaultVersion.attributes_metadata,
        ...data.attributes_metadata,
      },
    };

    if (isCreateMode) {
      updateData.system_language_type = systemLanguageType;
    }

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...updateData },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Version");
      }
    } else {
      result = await create.mutateAsync({
        ...updateData,
        system_language_type: systemLanguageType,
      });
      if (!result || create.isError) {
        throw new Error("Failed to create Version");
      }
    }
    return result;
  };

  const onDelete = async () => {
    if (!id) {
      throw new Error("Version ID is required for deletion");
    }
    await deleteById.mutateAsync(id);
    if (deleteById.isError) {
      throw new Error("Failed to delete version");
    }
  };

  const deleteWarningMessage = "Are you sure you want to delete this version?";

  const navigateBack = () => navigate(-1);

  const getEditRoute = () => `/versions/${systemLanguageType}/${id}/edit`;

  return {
    fetchData,
    userEnums,

    displayName,
    viewTitle,
    editTitle,
    title: viewTitle,

    defaultValues: defaultVersion,
    metadataFieldNames,
    isCreateMode,
    onSubmit,
    onDelete,
    deleteWarningMessage,
    formCallback: navigateBack,

    getEditRoute,
  };
};

export default useVersionController;
